import { observer } from "mobx-react";
import React from "react";

import { useTranslation } from "react-i18next";

import classnames from "classnames";

export interface ISegmentedGraphProps {
	title?: string;
	subtitle?: string;
	className?: string;
	data?: {
		homeValue?: number;
		homeLabel?: string;
		drawValue?: number;
		dwawLabel?: string;
		awayValue?: number;
		awayLabel?: string;
	};
	removeBodyPadding?: boolean;
	isPercentage?: boolean;
	hideHomeTitle?: boolean;
	hideDrawTitle?: boolean;
	hideAwayTitle?: boolean;
}

export default observer(function SegmentedGraph(props: ISegmentedGraphProps) {
	const { t } = useTranslation();

	const getValue = (value?: number) => {
		return `${value || 0}${props.isPercentage ? "%" : ""}`;
	};

	return (
		<div
			className={classnames(
				"card card--raised card--raised--framed card--stats",
				props.className
			)}
			data-type="segmented"
			data-at="at-SegmentedGraph"
		>
			{(props.title || props.subtitle) && (
				<div className="card__header card--stats__header">
					<div className="card__title card__title--base card--stats__heading">
						{props.title}
					</div>

					{props.subtitle && (
						<div className="card--stats__subtitle">
							{props.subtitle}
						</div>
					)}
				</div>
			)}

			<div className="card--stats__body">
				<div className="card--stats__section">
					<div className="card--stats__row u-gap--hor--2">
						<div className="card--stats__col card--stats__col--equal card--stats__col--ver card--stats__col--left">
							{!props.hideHomeTitle && (
								<div className="card--stats__label">
									{t("EVENT.OFFER.COMPETITOR_HOME")}
								</div>
							)}
							<div>
								{props.data?.homeLabel && (
									<>
										<span className="card--stats__label">
											{props.data?.homeLabel}
										</span>
										<span className="u-mar--x--1 u-color--text--tertiary">
											-
										</span>
									</>
								)}
								<span className="card--stats__value u-color--stats--home">
									{getValue(props.data?.homeValue)}
								</span>
							</div>
						</div>

						<div className="card--stats__col card--stats__col--equal card--stats__col--ver card--stats__col--middle">
							{!props.hideDrawTitle && (
								<div className="card--stats__label">
									{t("EVENT.STATISTICS.DRAW")}
								</div>
							)}
							<div>
								{props.data?.dwawLabel && (
									<>
										<span className="card--stats__label">
											{props.data?.dwawLabel}
										</span>
										<span className="u-mar--x--1 u-color--text--tertiary">
											-
										</span>
									</>
								)}
								<span className="card--stats__value u-color--stats--draw">
									{getValue(props.data?.drawValue)}
								</span>
							</div>
						</div>

						<div className="card--stats__col card--stats__col--equal card--stats__col--ver card--stats__col--right">
							{!props.hideAwayTitle && (
								<div className="card--stats__label">
									{t("EVENT.OFFER.COMPETITOR_AWAY")}
								</div>
							)}
							<div>
								{props.data?.awayLabel && (
									<>
										<span className="card--stats__label">
											{props.data?.awayLabel}
										</span>
										<span className="u-mar--x--1 u-color--text--tertiary">
											-
										</span>
									</>
								)}
								<span className="card--stats__value u-color--stats--away">
									{getValue(props.data?.awayValue)}
								</span>
							</div>
						</div>
					</div>

					<div className="card--stats__row u-gap--hor--2">
						<div
							className="card--stats__col card--stats__col--equal"
							style={{
								maxWidth: `${props.data?.homeValue || 100}%`,
							}}
						>
							<div className="chart chart--left is-home">
								<label
									htmlFor="chartHome"
									className="chart__value"
									hidden={true}
								>
									{getValue(props.data?.homeValue)}
								</label>
								<progress
									id="chartHome"
									className="chart__progress"
									value="100"
									max="100"
								/>
							</div>
						</div>

						<div
							className="card--stats__col card--stats__col--equal"
							style={{
								maxWidth: `${props.data?.drawValue || 100}%`,
							}}
						>
							<div className="chart chart--center is-draw">
								<label
									htmlFor="chartDraw"
									className="chart__value"
									hidden={true}
								>
									{getValue(props.data?.drawValue)}
								</label>
								<progress
									id="chartDraw"
									className="chart__progress"
									value="100"
									max="100"
								/>
							</div>
						</div>

						<div
							className="card--stats__col card--stats__col--equal"
							style={{
								maxWidth: `${props.data?.awayValue || 100}%`,
							}}
						>
							<div className="chart chart--right is-away">
								<label
									htmlFor="chartAway"
									className="chart__value"
									hidden={true}
								>
									{getValue(props.data?.awayValue)}
								</label>
								<progress
									id="chartAway"
									className="chart__progress"
									value="100"
									max="100"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});
